/**
 * Button atom — Invirtus action-control SSOT.
 *
 * Prefer: `import { Button } from '@/components/ui/atoms/Button'`
 *
 * Design notes:
 * - Visual `type` ≠ native `htmlType` (use `htmlType` for submit/reset).
 * - M2 API name `contained` maps to M3 Filled (see Button.types.ts) — kept to
 *   avoid a breaking rename across consumers.
 * - Interaction ink lives on `::after` (M3 state layer). Selected is a separate
 *   resting container (`data-selected`), never faked via `:active` or a type swap.
 * - Every type uses brand `border-1.5` (inset stroke). `dashed` is an
 *   add-zone modifier on `outlined` / `icon` only (transparent fill, 2px
 *   dash, glyph/label = dash color).
 * - Icon glyph size follows `size` via BUTTON_ICON_GLYPH_CLASS (pass bare icons).
 * - Loading keeps layout stable (content invisible, spinner centered, aria-busy).
 * - Icon-only needs an accessible name (`aria-label` / `label` / `title`).
 * - Links: style Router `Link` with `getButtonClasses` until asChild exists.
 *
 * Brand-only (not M3): `rightIconWithBackground` trailing plate for hero CTAs.
 * Only the plate is right-anchored when the button is stretched; a plain
 * `rightIcon` flows inline with the label like a leading icon.
 * Segmented controls are out of scope — use a separate molecule.
 */

import React from 'react';
import { Tooltip } from '../../tooltip';
import { cn } from '@/utils/cn';
import { getButtonClasses } from './Button.styles';
import {
  BUTTON_ICON_GLYPH_CLASS,
  BUTTON_RIGHT_ICON_PLATE,
  BUTTON_SPINNER_SIZE_CLASS,
} from './Button.size';
import type { ButtonProps, ButtonSize } from './Button.types';

export type {
  ButtonProps,
  ButtonType,
  ButtonDashedType,
  ButtonIntent,
  ButtonSize,
  ButtonHtmlType,
  ButtonTooltipVariant,
} from './Button.types';

/** Apply Button `size` glyph dimensions to a single icon element.
 * Strips caller h-/w-/size-* (and Lucide `size` prop) so the atom owns glyph scale.
 */
const ICON_SIZE_CLASS_RE =
  /^(?:(?:sm|md|lg|xl|2xl):)?(?:min-|max-)?(?:h|w|size)-/;

const stripIconSizeClasses = (className?: string): string => {
  if (!className) return '';
  return className
    .split(/\s+/)
    .filter(Boolean)
    .filter((token) => !ICON_SIZE_CLASS_RE.test(token))
    .join(' ');
};

const scaleIcon = (node: React.ReactNode, size: ButtonSize): React.ReactNode => {
  if (!React.isValidElement<{ className?: string; size?: number | string }>(node)) {
    return node;
  }
  const { className, size: _lucideSize, ...rest } = node.props;
  return React.cloneElement(node, {
    ...rest,
    size: undefined,
    className: cn(
      BUTTON_ICON_GLYPH_CLASS[size],
      'shrink-0',
      stripIconSizeClasses(className)
    ),
  } as Partial<{ className?: string; size?: number | string }>);
};

const LoadingSpinner: React.FC<{ size: ButtonSize; className?: string }> = ({
  size,
  className,
}) => (
  <span
    className={cn(
      'animate-spin border-current border-t-transparent rounded-full',
      BUTTON_SPINNER_SIZE_CLASS[size],
      className
    )}
    aria-hidden
  />
);

const RightIconWrapper: React.FC<{ size: ButtonSize; children: React.ReactNode }> = ({
  size,
  children,
}) => (
  <span
    className={cn(
      'inline-flex shrink-0 items-center justify-center rounded-full bg-light text-dark-invirtus',
      BUTTON_RIGHT_ICON_PLATE[size]
    )}
    aria-hidden
  >
    {children}
  </span>
);

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      id,
      label,
      labelClassName = '',
      title,
      icon,
      rightIcon,
      rightIconWithBackground = false,
      spreadContent = false,
      onClick,
      type = 'contained',
      intent = 'primary',
      size = 'md',
      disabled = false,
      loading = false,
      selected = false,
      dashed = false,
      className = '',
      tooltipVariant = 'default',
      htmlType = 'button',
      children,
      'aria-label': ariaLabel,
      'aria-pressed': ariaPressed,
      ...rest
    },
    ref
  ) => {
    const isIconOnly = type === 'icon' || (!label && !!icon && !rightIcon && !children);
    const isInactive = disabled || loading;
    const hasTrailingPlate =
      rightIconWithBackground && type !== 'icon' && !!rightIcon;
    const buttonClasses = getButtonClasses(
      type,
      intent,
      size,
      isInactive,
      className,
      selected,
      dashed,
      hasTrailingPlate
    );

    const resolvedAriaLabel =
      ariaLabel ?? (isIconOnly ? label || title : undefined);

    // selected → pressed for AT; allow explicit aria-pressed override (e.g. toggle off)
    const resolvedAriaPressed =
      ariaPressed ?? (selected ? true : undefined);

    const scaledIcon = icon ? scaleIcon(icon, size) : null;
    const scaledRightIcon = rightIcon ? scaleIcon(rightIcon, size) : null;

    const labelNode =
      !isIconOnly && label ? (
        <span className={labelClassName}>{label}</span>
      ) : null;

    const hasAnchoredTrailingPlate =
      !isIconOnly && !!scaledRightIcon && rightIconWithBackground;

    // Label group left, rightIcon right, across the button's full width.
    const isSpread =
      !isIconOnly && !!scaledRightIcon && spreadContent && !hasAnchoredTrailingPlate;

    // Above hit-target (::before) and state-layer (::after)
    const content = (
      <span
        className={cn(
          'relative z-[2]',
          hasAnchoredTrailingPlate || isSpread
            ? 'flex w-full min-w-0 items-center gap-[inherit]'
            : 'inline-flex items-center justify-center gap-[inherit]',
          isSpread && 'justify-between',
          loading && 'invisible'
        )}
      >
        {isSpread ? (
          <>
            <span className="flex min-w-0 items-center gap-[inherit]">
              {scaledIcon}
              {labelNode}
              {children}
            </span>
            {scaledRightIcon}
          </>
        ) : hasAnchoredTrailingPlate ? (
          <>
            <span className="flex min-w-0 flex-1 items-center justify-center gap-[inherit]">
              {scaledIcon}
              {labelNode}
              {children}
            </span>
            <span className="shrink-0">
              <RightIconWrapper size={size}>{scaledRightIcon}</RightIconWrapper>
            </span>
          </>
        ) : (
          <>
            {scaledIcon}
            {labelNode}
            {scaledRightIcon}
            {children}
          </>
        )}
      </span>
    );

    const buttonElement = (
      <button
        id={id}
        ref={ref}
        type={htmlType}
        onClick={onClick}
        disabled={isInactive}
        className={buttonClasses}
        {...rest}
        aria-busy={loading || undefined}
        aria-disabled={isInactive || undefined}
        aria-label={resolvedAriaLabel}
        aria-pressed={resolvedAriaPressed}
        data-selected={selected || undefined}
      >
        {content}
        {loading && (
          <span
            className="absolute inset-0 z-[2] flex items-center justify-center"
            aria-hidden
          >
            <LoadingSpinner size={size} />
          </span>
        )}
      </button>
    );

    // The tooltip wrapper is inline-flex, so it shrink-wraps and a stretched
    // button would resolve `w-full` against content width. Forward the stretch.
    return title ? (
      <Tooltip
        content={title}
        variant={tooltipVariant}
        triggerClassName={/(^|\s)w-full(\s|$)/.test(className) ? 'w-full' : ''}
      >
        {buttonElement}
      </Tooltip>
    ) : (
      buttonElement
    );
  }
);

Button.displayName = 'Button';

export default Button;
